import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * Whether a slot is being offered.
 *
 * Two values, and the pair is load-bearing rather than a boolean's two states
 * spelled out: **closing withdraws availability and strands nobody, while
 * deleting is refused once a booking names the row** (P25c). An `isOffered`
 * boolean would say the same thing today and have nowhere to put the third
 * answer this domain will want — a slot the partner cancelled, say, which is not
 * the same as one staff never opened. P25a2's own lesson, applied before the
 * column exists rather than after.
 */
export const SLOT_STATUSES = ['open', 'closed'] as const;

export const VenueSlotStatusSchema = z.enum(SLOT_STATUSES);

export type VenueSlotStatus = (typeof SLOT_STATUSES)[number];

/**
 * The most concurrent supervised meetings one slot may hold.
 *
 * A **sanity** bound, not a policy dial — the class `MAX_PAGE_SIZE` belongs to.
 * A venue offering more than a hundred simultaneous supervised meetings is a
 * mistyped number, and the operator should be told so by the field rather than
 * by a partner. What the deployment actually wants is expressed by how many
 * slots it opens, which is data, not a ceiling.
 */
export const VENUE_SLOT_MAX_CAPACITY = 100;

/**
 * One window a venue offers, as its payload carries it (`03-meetings.md`).
 *
 * ## Both ends are INSTANTS, and that is the whole timezone story
 *
 * `starts_at` is what a member is eventually told out loud, so an hour of drift
 * is a member arriving to a locked door. The api stores UTC wall-clock strings
 * and converts once on read (N3); the admin converts once more, inside
 * `DateTimeInput`, over `local-instant.util`. Nothing in between ever holds a
 * zone-less wall clock, which is why this schema refuses one.
 *
 * A venue carries **no timezone column** — the deployment has one zone
 * (`DEFAULT_TIMEZONE`) and one market (U16). The day a brand runs venues across
 * two zones, the column goes on `meetings.venues` and this contract gains
 * nothing: an instant is already unambiguous, and only the *rendering* would
 * change.
 *
 * ## What this does NOT re-check
 *
 * `capacity` carries no ceiling here. A read DTO describes what the server
 * **sends**, so re-validating a write-side bound makes a stored row unreadable
 * the day the bound is lowered or a seeded venue carries a bigger number
 * (P25a1's lesson, and why `VenueSchema.contactEmail` is a plain string). The
 * bounds live on {@link VenueSlotWritableShape}, where the value arrives from a
 * person.
 */
export const VenueSlotSchema = z
  .object({
    id: UuidSchema,
    venueId: UuidSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    capacity: z.number().int(),
    status: VenueSlotStatusSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type VenueSlot = z.infer<typeof VenueSlotSchema>;
